import "lib/@hooks/useEvent"
import { useReducer } from "react"
import { useScheduleEditorIds } from "./use-schedule-editor-ids"
import { readScheduleEditorSession } from "../lib/schedule-editor-session"
import { AmendsChanged, SubstantiveEditsChanged } from "../schedule-editor-event-definitions.mjs"
import { hasAudienceAmendsContent } from "../../amends/lib/audience-aware-amends.mjs"
import { CURRENT_CYCLE_EXCLUDED_AMENDS_KEYS } from "../lib/current-cycle-excluded-amends-keys"

/**
 * Returns true when the current draft has something worth reviewing — used to gate
 * Compare and Submit For Review.
 *
 * Two independent signals count as a change:
 *
 * 1. `hasSubstantiveEdits` — schedule content differs from the published compare source.
 *    Set by an initial compare, by syncSubstantiveEditsFromCompare() after a persisted
 *    save, by the SaveSkippedUnchanged re-compare, and by DraftReloaded (schedule-editor.js).
 *    `null` means compare data is still loading.
 *
 * 2. An edited author note (amend) in the current review cycle. Amends are stored as
 *    AuthoringAmends rows, not as schedule content, so `buildDraftSnapshot` cannot see
 *    them — a draft whose only change is an author note used to read as unchanged and
 *    could never be submitted, even though `leadingSummary` is itself always required
 *    before submitting. Only current-cycle (`persistedAmends`) notes count: amends
 *    inherited from a previous cycle would otherwise make a freshly-rejected draft look
 *    changed before the author has touched it.
 *
 * The amends signal applies to Submit only. Compare renders a *content* diff, which is
 * empty for an amends-only change, so `forCompare` keeps asking the content question
 * alone and keeps its "There's nothing to compare" tooltip.
 *
 * Does NOT treat server `isDraft` as "has edits" — a draft record can exist with
 * content identical to published (see testproject-submit-no-changes).
 *
 * @param {{ forCompare?: boolean }} [options]
 * @returns {boolean}
 */
export function useHasSavedEdits({ forCompare = false } = {}) {
    const { draftId } = useScheduleEditorIds()
    const [, forceUpdate] = useReducer(tick, 0)

    SubstantiveEditsChanged(draftId).useEvent(forceUpdate)
    AmendsChanged(draftId).useEvent(forceUpdate)

    const session = readScheduleEditorSession(draftId)
    const value = session?.hasSubstantiveEdits

    if (forCompare) {
        return value === true
    }

    if (value === false) {
        return (session?.persistedAmends ?? []).some(isEditedAmend)
    }

    return true
}

function isEditedAmend(amend) {
    const payload = amend?.payload
    if (!payload) return false
    // draft.submitted / draft.rejected / draft.approved / draft.returnedToEditing are
    // workflow markers written by the review lifecycle, not author edits.
    if (CURRENT_CYCLE_EXCLUDED_AMENDS_KEYS.has(payload.amendsKey)) return false
    return hasAudienceAmendsContent(payload)
}

function tick(n) {
    return n + 1
}
